import {
  EntityModel,
  ParticipantGroupType,
  prisma,
  type Prisma,
} from "client";
import { isMongoObjectId } from "@sajilni/utils/ticketQr";

// CREATE

export const createProduct = async (
  userId: string,
  data: Prisma.ProductCreateInput
) => {
  try {
    return await prisma.product.create({
      data: { ...data, createdBy: { connect: { id: userId } } },
    });
  } catch (error) {
    console.error("Failed to create product:", error);
    throw error;
  }
};

// GET

export const getProduct = async (id: string) => {
  return await prisma.product.findUnique({
    where: { id },
    include: { translations: true },
  });
};

export const getProductsByEntityId = async (
  entityId: string,
  locale?: string,
  search?: string,
  userId?: string,
  eventId?: string
) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const searchParam = search ? escapeRegex(search) : "";
  const filter = searchParam
    ? {
        OR: [
          {
            translations: {
              some: {
                OR: [
                  {
                    name: {
                      contains: searchParam,
                      mode: "insensitive" as const,
                    },
                  }, // Add 'as const'
                ],
              },
            },
          },
        ],
      }
    : {};
  const products = await prisma.product.findMany({
    where: { entityId, ...filter },
    include: {
      // Without userId, Prisma drops the where clause and returns every user's bookmarks.
      bookmarks: userId
        ? {
            where: {
              entityModel: EntityModel.PRODUCT,
              userId,
              ...(eventId ? { eventId } : {}),
            },
          }
        : false,
      translations: {
        where: {
          language: {
            code: locale,
          },
        },
      },
    },
  });
  return products.flatMap((product) => {
    const translation = product.translations[0];
    if (!translation) return [];
    return [
      {
        ...product,
        bookmarks: userId ? (product.bookmarks ?? []) : [],
        name: translation.name,
        description: translation.description,
      },
    ];
  });
};

export type PortalProductRow = {
  id: string;
  name: string;
  description: string;
  link: string | null;
  image: string | null;
  translationId?: string;
  languageId?: string;
  translations: {
    id: string;
    name: string;
    description: string | null;
    languageId: string;
  }[];
};

export type ProductSearchPage = {
  items: PortalProductRow[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
};

export const PRODUCT_PAGE_SIZE = 12;

const portalProductSelect = {
  id: true,
  link: true,
  image: true,
  translations: {
    select: {
      id: true,
      name: true,
      description: true,
      languageId: true,
      language: { select: { code: true } },
    },
  },
} as const;

const mapPortalProducts = (
  products: {
    id: string;
    link: string | null;
    image: string | null;
    translations: {
      id: string;
      name: string;
      description: string | null;
      languageId: string;
      language: { code: string };
    }[];
  }[],
  locale: string
): PortalProductRow[] =>
  products.flatMap((product) => {
    if (!product.translations.length) return [];
    const display =
      product.translations.find((t) => t.language.code === locale) ??
      product.translations[0];
    return [
      {
        id: product.id,
        link: product.link,
        image: product.image,
        name: display.name,
        description: display.description ?? "",
        translationId: display.id,
        languageId: display.languageId,
        translations: product.translations.map(
          ({ id, name, description, languageId }) => ({
            id,
            name,
            description,
            languageId,
          })
        ),
      },
    ];
  });

const buildProductSearchWhere = (entityId: string, search?: string) => {
  const escapeRegex = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const searchParam = search?.trim() ? escapeRegex(search.trim()) : "";
  return {
    entityId,
    translations: searchParam
      ? {
          some: {
            OR: [
              {
                name: {
                  contains: searchParam,
                  mode: "insensitive" as const,
                },
              },
              {
                description: {
                  contains: searchParam,
                  mode: "insensitive" as const,
                },
              },
            ],
          },
        }
      : { some: {} },
  };
};

export const searchProductsByEntityId = async ({
  entityId,
  locale,
  search = "",
  page = 1,
  pageSize = PRODUCT_PAGE_SIZE,
}: {
  entityId: string;
  locale: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ProductSearchPage> => {
  const where = buildProductSearchWhere(entityId, search);
  const safePage = Math.max(1, page);
  const safePageSize = Math.min(Math.max(1, pageSize), 50);

  const [total, products] = await Promise.all([
    prisma.product.count({ where }),
    prisma.product.findMany({
      where,
      select: portalProductSelect,
      orderBy: { createdAt: "desc" },
      skip: (safePage - 1) * safePageSize,
      take: safePageSize,
    }),
  ]);

  return {
    items: mapPortalProducts(products, locale),
    total,
    page: safePage,
    pageSize: safePageSize,
    hasMore: safePage * safePageSize < total,
  };
};

export const getPortalProductsByEntityId = async (
  entityId: string,
  locale: string
): Promise<PortalProductRow[]> => {
  const { items } = await searchProductsByEntityId({
    entityId,
    locale,
    page: 1,
    pageSize: PRODUCT_PAGE_SIZE,
  });
  return items;
};

export const getParticipantEntityIdForPortal = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType
) => {
  const where = isMongoObjectId(eventId)
    ? { eventId, teamIds: { has: userId } }
    : { event: { slug }, teamIds: { has: userId } };

  if (type === ParticipantGroupType.EXHIBITORS) {
    return prisma.exhibitor.findFirst({ where, select: { id: true } });
  }
  if (type === ParticipantGroupType.SPONSORS) {
    return prisma.sponsor.findFirst({ where, select: { id: true } });
  }
  return null;
};

export const getParticipantProductsForPortal = async (
  slug: string,
  userId: string,
  eventId: string,
  type: ParticipantGroupType,
  locale: string,
  search = "",
  page = 1,
  pageSize = PRODUCT_PAGE_SIZE
) => {
  const participant = await getParticipantEntityIdForPortal(
    slug,
    userId,
    eventId,
    type
  );
  if (!participant) return null;

  const productsPage = await searchProductsByEntityId({
    entityId: participant.id,
    locale,
    search,
    page,
    pageSize,
  });

  return { id: participant.id, ...productsPage };
};

export const assertProductBelongsToEntity = async (
  productId: string,
  entityId: string
) => {
  const product = await prisma.product.findFirst({
    where: { id: productId, entityId },
    select: { id: true, entityType: true },
  });
  return product;
};

export const assertUserCanManageProductEntity = async (
  entityId: string,
  userId: string
) => {
  const exhibitor = await prisma.exhibitor.findUnique({
    where: { id: entityId },
    select: { eventId: true },
  });
  if (exhibitor?.eventId) {
    const allowed = await prisma.exhibitor.findFirst({
      where: { id: entityId, teamIds: { has: userId } },
      select: { id: true },
    });
    return allowed ? { entityId, entityType: "EXHIBITOR" as const, eventId: exhibitor.eventId } : null;
  }

  const sponsor = await prisma.sponsor.findUnique({
    where: { id: entityId },
    select: { eventId: true },
  });
  if (sponsor?.eventId) {
    const allowed = await prisma.sponsor.findFirst({
      where: { id: entityId, teamIds: { has: userId } },
      select: { id: true },
    });
    return allowed ? { entityId, entityType: "SPONSOR" as const, eventId: sponsor.eventId } : null;
  }

  return null;
};

// UPDATE

export const updateProduct = async (
  id: string,
  data: Prisma.ProductUpdateInput
) => {
  return await prisma.product.update({
    where: { id },
    data,
  });
};

// DELETE

export const deleteProduct = async (id: string) => {
  await prisma.bookmark.deleteMany({
    where: { entityId: id, entityModel: EntityModel.PRODUCT },
  });
  await prisma.productTranslations.deleteMany({ where: { productId: id } });
  return await prisma.product.delete({ where: { id } });
};
